import { LAYOUT, type LayoutId } from "@aybuben/core/writing/layouts.ts";
import { Delete } from "lucide-react";
import { useEffect, useRef, type MouseEvent, type PointerEvent } from "react";

import { useStrings } from "../../i18n/index.tsx";
import { ARM, COL, FOCUS } from "../../theme.ts";

interface KeyboardProps {
	layout: LayoutId;
	/** Letters the learner has met; the rest are dimmed but still type. null dims none. */
	known: ReadonlySet<string> | null;
	onType: (text: string) => void;
	onBackspace: () => void;
}

/** Shorter keys on a short screen, so the whole keyboard fits above the lesson's action panel. */
const KEY_H = "clamp(36px, 5.5vh, 46px)";

/** Between keys, as on a phone's own keyboard. */
const GAP = 5;

/** Holding backspace deletes on: once, then after a pause, then steadily. */
const HOLD_DELAY = 400;
const HOLD_EVERY = 80;

/**
 * The in-app Armenian keyboard: real buttons, so a screen reader names each
 * letter and a keyboard user can tab to them. Keys never take focus from a
 * pointer (mousedown is cancelled), so a laptop's space bar never presses the
 * last letter tapped again.
 */
export function Keyboard({ layout, known, onType, onBackspace }: KeyboardProps) {
	const t = useStrings();
	const hold = useRef<{
		delay?: ReturnType<typeof setTimeout>;
		every?: ReturnType<typeof setInterval>;
	}>({});
	const stopHold = () => {
		clearTimeout(hold.current.delay);
		clearInterval(hold.current.every);
		hold.current = {};
	};
	// Unmounted mid-hold (the step was checked by Enter): stop deleting.
	useEffect(() => stopHold, []);

	const keep = (e: MouseEvent) => e.preventDefault();

	// Pointer presses act on pointerdown, so holding repeats; a click with no
	// pointer behind it (detail 0: Enter or Space on a focused key) acts once.
	const backDown = (e: PointerEvent) => {
		if (e.button !== 0) return;
		stopHold();
		onBackspace();
		hold.current.delay = setTimeout(() => {
			hold.current.every = setInterval(onBackspace, HOLD_EVERY);
		}, HOLD_DELAY);
	};
	const backClick = (e: MouseEvent) => {
		if (e.detail === 0) onBackspace();
	};

	// Every letter key is as wide as one column of the widest row, so a
	// shorter row is centred rather than stretched.
	const cols = Math.max(...LAYOUT[layout].rows.map((r) => r.length));
	const keyWidth = `calc((100% - ${(cols - 1) * GAP}px) / ${cols})`;

	const key = (ch: string) => {
		const dim = known !== null && !known.has(ch);
		return (
			<button
				key={ch}
				type="button"
				onMouseDown={keep}
				onClick={() => onType(ch)}
				className={`flex items-center justify-center rounded-lg ${FOCUS}`}
				style={{
					flex: `0 0 ${keyWidth}`,
					minWidth: 0,
					height: KEY_H,
					fontFamily: ARM,
					fontSize: 22,
					lineHeight: 1,
					touchAction: "manipulation",
					background: COL.card,
					color: dim ? COL.faint : COL.ink,
					border: `1px solid ${COL.line}`,
					boxShadow: `0 2px 0 ${dim ? COL.line : COL.edge}`,
				}}
			>
				{ch}
			</button>
		);
	};

	return (
		<div
			role="group"
			aria-label={t("write.keys")}
			data-testid="keyboard"
			data-layout={layout}
			className="flex flex-col rounded-2xl px-1 py-1.5 select-none"
			style={{ gap: GAP + 2, background: COL.keys }}
		>
			{LAYOUT[layout].rows.map((row, i) => (
				<div key={i} className="flex justify-center" style={{ gap: GAP }}>
					{row.map(key)}
				</div>
			))}
			<div className="flex justify-center" style={{ gap: GAP }}>
				<button
					type="button"
					onMouseDown={keep}
					onClick={() => onType(" ")}
					aria-label={t("write.space")}
					className={`rounded-lg ${FOCUS}`}
					style={{
						flex: 5,
						height: KEY_H,
						touchAction: "manipulation",
						background: COL.card,
						border: `1px solid ${COL.line}`,
						boxShadow: `0 2px 0 ${COL.edge}`,
					}}
				/>
				<button
					type="button"
					onMouseDown={keep}
					onPointerDown={backDown}
					onPointerUp={stopHold}
					onPointerLeave={stopHold}
					onPointerCancel={stopHold}
					onClick={backClick}
					onContextMenu={keep}
					aria-label={t("write.backspace")}
					className={`flex items-center justify-center rounded-lg ${FOCUS}`}
					style={{
						flex: 2,
						height: KEY_H,
						touchAction: "manipulation",
						background: COL.grey,
						color: COL.ink,
						border: `1px solid ${COL.line}`,
						boxShadow: `0 2px 0 ${COL.greyDark}`,
					}}
				>
					<Delete size={22} aria-hidden="true" />
				</button>
			</div>
		</div>
	);
}
